/**
 * Tour target keys wired in learner UI via `data-tour-id="..."`.
 * Update this file whenever a new element is wired — admins see these as suggestions
 * but may enter any key manually in Tour Management.
 *
 * Shared chrome (bottom banner, side nav) applies to every page.
 */

export interface WiredTourTarget {
  /** Must match data-tour-id on the DOM element */
  key: string;
  labelEn?: string;
  labelFa?: string;
  /** Optional CSS selector override */
  selector?: string;
}

/** Present on most logged-in learner views */
export const SHARED_WIRED_TOUR_TARGETS: WiredTourTarget[] = [
  { key: 'bottom-banner-help', labelEn: 'Help button (bottom banner)', labelFa: 'دکمه راهنما (نوار پایین)' },
  { key: 'bottom-banner-search', labelEn: 'Search (bottom banner)', labelFa: 'جستجو (نوار پایین)' },
  { key: 'bottom-banner-keep-learning', labelEn: 'Keep learning (bottom banner)', labelFa: 'ادامه یادگیری (نوار پایین)' },
  { key: 'edge-telegram', labelEn: 'Telegram bot button', labelFa: 'دکمه ربات تلگرام' },
  { key: 'edge-actions-close', labelEn: 'Hide edge buttons', labelFa: 'پنهان کردن دکمه‌های لبه' },
  { key: 'edge-actions-open', labelEn: 'Show edge buttons', labelFa: 'نمایش دکمه‌های لبه' },
  { key: 'bottom-banner-settings', labelEn: 'Settings (bottom banner)', labelFa: 'تنظیمات (نوار پایین)' },
  { key: 'bottom-banner-dashboard', labelEn: 'Dashboard button (bottom banner)', labelFa: 'دکمه داشبورد (نوار پایین)' },
  { key: 'bottom-banner-home', labelEn: 'Home button (bottom banner)', labelFa: 'دکمه خانه (نوار پایین)' },
  { key: 'bottom-banner-collapse', labelEn: 'Collapse banner (bottom banner)', labelFa: 'جمع کردن نوار پایین' },
  { key: 'side-nav-menu', labelEn: 'Side menu button', labelFa: 'دکمه منوی کناری' },
  { key: 'side-nav-panel', labelEn: 'Side menu panel', labelFa: 'پنل منوی کناری' },
  { key: 'side-nav-public-pages', labelEn: 'Public pages (side nav)', labelFa: 'صفحات عمومی (منوی کناری)' },
  { key: 'side-nav-leitner', labelEn: 'Leitner (side nav)', labelFa: 'لایتنر (منوی کناری)' },
  { key: 'side-nav-telegram', labelEn: 'Telegram (side nav)', labelFa: 'تلگرام (منوی کناری)' },
  { key: 'side-nav-chat', labelEn: 'AI chat (side nav)', labelFa: 'گفتگوی هوش مصنوعی (منوی کناری)' },
  { key: 'header-theme-toggle', labelEn: 'Dark / light mode', labelFa: 'حالت تاریک / روشن' },
  { key: 'header-language-toggle', labelEn: 'Language toggle', labelFa: 'تغییر زبان' },
  { key: 'header-profile', labelEn: 'Profile button (header photo)', labelFa: 'دکمه پروفایل (عکس سرصفحه)' },
  { key: 'header-logout', labelEn: 'Logout button', labelFa: 'دکمه خروج' },
  { key: 'header-chatbot', labelEn: 'AI chatbot button', labelFa: 'دکمه چت هوش مصنوعی' },
  { key: 'header-leitner', labelEn: 'Leitner box button', labelFa: 'دکمه جعبه لایتنر' },
  { key: 'footer-version-journey', labelEn: 'Version journey button', labelFa: 'دکمه سفر نسخه‌ها' },
];

/** Shared by both quick-settings tours. Simple highlights the list row; Advanced highlights the tab. */
const QUICK_SETTINGS_WIRED_TARGETS: WiredTourTarget[] = [
  { key: 'quick-settings-security', labelEn: 'Security', labelFa: 'امنیت' },
  { key: 'quick-settings-security-methods', labelEn: 'Sign-in methods', labelFa: 'روش‌های ورود' },
  { key: 'quick-settings-security-setup', labelEn: 'Method setup', labelFa: 'راه‌اندازی روش' },
  { key: 'quick-settings-notifications', labelEn: 'Notifications', labelFa: 'اعلان‌ها' },
  { key: 'quick-settings-notifications-email', labelEn: 'Email updates', labelFa: 'به‌روزرسانی ایمیل' },
  { key: 'quick-settings-notifications-alerts', labelEn: 'In-app alerts', labelFa: 'اعلان‌های درون‌برنامه' },
  { key: 'quick-settings-lesson-text', labelEn: 'Lesson text', labelFa: 'متن درس' },
  { key: 'quick-settings-lesson-text-fonts', labelEn: 'Lesson fonts', labelFa: 'قلم‌های درس' },
  { key: 'quick-settings-lesson-text-size', labelEn: 'Reading size', labelFa: 'اندازه خواندن' },
  { key: 'quick-settings-lesson-text-preview', labelEn: 'Text preview', labelFa: 'پیش‌نمایش متن' },
  { key: 'quick-settings-region', labelEn: 'Region & language', labelFa: 'منطقه و زبان' },
  { key: 'quick-settings-region-language', labelEn: 'App language', labelFa: 'زبان برنامه' },
  { key: 'quick-settings-region-timezone', labelEn: 'Time zone', labelFa: 'منطقه زمانی' },
  { key: 'quick-settings-template', labelEn: 'Preferences', labelFa: 'ترجیحات' },
  { key: 'quick-settings-template-choices', labelEn: 'App theme', labelFa: 'قالب برنامه' },
  { key: 'quick-settings-template-mode', labelEn: 'Light and dark mode', labelFa: 'حالت روشن و تاریک' },
];

/** Per-page wired targets — add entries here when you wire data-tour-id in components */
export const WIRED_TOUR_TARGETS_BY_PAGE: Record<string, WiredTourTarget[]> = {
  'dashboard-simple': [
    { key: 'mme-design-bar', labelEn: 'Template', labelFa: 'قالب' },
    { key: 'mme-continue', labelEn: 'Continue', labelFa: 'ادامه' },
    { key: 'mme-tile-courses', labelEn: 'My courses', labelFa: 'دوره‌های من' },
    { key: 'mme-tile-practice', labelEn: 'Practice', labelFa: 'تمرین' },
    { key: 'mme-tile-library', labelEn: 'Library', labelFa: 'کتابخانه' },
    { key: 'mme-tile-badges', labelEn: 'Badges', labelFa: 'نشان‌ها' },
    { key: 'mme-tile-progress', labelEn: 'My progress', labelFa: 'پیشرفت من' },
    { key: 'mme-tile-points', labelEn: 'Experience points', labelFa: 'امتیاز تجربه' },
    { key: 'mme-tile-placement', labelEn: 'Knowledge placement', labelFa: 'تعیین سطح' },
    { key: 'mme-tile-recommended', labelEn: 'Recommended', labelFa: 'پیشنهادی' },
    { key: 'mme-me', labelEn: 'Me', labelFa: 'من' },
    { key: 'bottom-banner-search', labelEn: 'Search', labelFa: 'جستجو' },
    { key: 'bottom-banner-keep-learning', labelEn: 'Keep learning', labelFa: 'ادامه یادگیری' },
    { key: 'bottom-banner-settings', labelEn: 'Settings', labelFa: 'تنظیمات' },
  ],
  dashboard: [
    {
      key: 'dashboard-hero',
      labelEn: 'Learning dashboard hero',
      labelFa: 'بخش قهرمان داشبورد یادگیری',
    },
    { key: 'dashboard-welcome', labelEn: 'Welcome card', labelFa: 'کارت خوش‌آمد' },
    { key: 'dashboard-quick-actions', labelEn: 'Quick actions', labelFa: 'میانبرها' },
    { key: 'dashboard-learning-stats', labelEn: 'Learning statistics', labelFa: 'آمار یادگیری' },
    { key: 'dashboard-courses', labelEn: 'Your courses section', labelFa: 'بخش دوره‌های شما' },
    { key: 'dashboard-library', labelEn: 'Library section', labelFa: 'بخش کتابخانه' },
    { key: 'dashboard-achievements', labelEn: 'Achievements section', labelFa: 'بخش دستاوردها' },
    { key: 'dashboard-experience-points', labelEn: 'Experience points section', labelFa: 'بخش امتیاز تجربه' },
    { key: 'dashboard-recent-activity', labelEn: 'Recent activity', labelFa: 'فعالیت اخیر' },
    { key: 'dashboard-placement', labelEn: 'Knowledge placement banner', labelFa: 'بنر تعیین سطح' },
    { key: 'mme-design-bar', labelEn: 'Simple / Advanced look', labelFa: 'نمای ساده / پیشرفته' },
  ],
  'courses-catalog': [
    { key: 'courses-catalog-header', labelEn: 'Courses header', labelFa: 'سرصفحه دوره‌ها' },
    { key: 'courses-catalog-list', labelEn: 'Course cards list', labelFa: 'فهرست کارت دوره‌ها' },
    { key: 'courses-catalog-progress', labelEn: 'Course progress', labelFa: 'پیشرفت دوره' },
  ],
  'courses-detail': [
    { key: 'courses-detail-header', labelEn: 'Course header card', labelFa: 'کارت سرصفحه دوره' },
    { key: 'courses-detail-progress', labelEn: 'Course progress', labelFa: 'پیشرفت دوره' },
    { key: 'courses-detail-levels', labelEn: 'Levels and lessons', labelFa: 'سطح‌ها و درس‌ها' },
    { key: 'courses-detail-exercises', labelEn: 'Course exercises panel', labelFa: 'پنل تمرین‌های دوره' },
    { key: 'courses-detail-quizzes', labelEn: 'Course quizzes panel', labelFa: 'پنل آزمون‌های دوره' },
  ],
  library: [
    { key: 'library-header', labelEn: 'Library header', labelFa: 'سرصفحه کتابخانه' },
    { key: 'library-view-toggle', labelEn: 'View style toggle', labelFa: 'تغییر نمای کتاب‌ها' },
    { key: 'library-filter-button', labelEn: 'Filters button', labelFa: 'دکمه فیلترها' },
    { key: 'library-filters', labelEn: 'Filters panel', labelFa: 'پنل فیلترها' },
    { key: 'library-book-grid', labelEn: 'Book list area', labelFa: 'فهرست کتاب‌ها' },
  ],
  book: [
    { key: 'book-viewer-header', labelEn: 'Book header', labelFa: 'سرصفحه کتاب' },
    { key: 'book-review-button', labelEn: 'Review button', labelFa: 'دکمه مرور' },
    { key: 'book-rating', labelEn: 'Rating & comments', labelFa: 'امتیاز و نظرات' },
    { key: 'book-favorite', labelEn: 'Favorite button', labelFa: 'دکمه علاقه‌مندی' },
    { key: 'book-view', labelEn: 'View count', labelFa: 'تعداد بازدید' },
    { key: 'book-chip-difficulty', labelEn: 'Difficulty chip', labelFa: 'برچسب سطح دشواری' },
    { key: 'book-chip-type', labelEn: 'Book type chip', labelFa: 'برچسب نوع کتاب' },
    { key: 'book-description', labelEn: 'Book description', labelFa: 'توضیحات کتاب' },
    { key: 'book-text', labelEn: 'Book content', labelFa: 'متن کتاب' },
    { key: 'book-tip', labelEn: 'Content tip', labelFa: 'نکته محتوا' },
    { key: 'book-sticky-audio', labelEn: 'Sticky audio row', labelFa: 'ردیف صوت چسبان' },
    { key: 'book-word-help', labelEn: 'Word help', labelFa: 'راهنمای واژه' },
    { key: 'book-word-help-scope', labelEn: 'Word help scope', labelFa: 'محدوده راهنمای واژه' },
  ],
  lesson: [
    { key: 'lesson-header', labelEn: 'Lesson header', labelFa: 'سرصفحه درس' },
    { key: 'lesson-rating', labelEn: 'Rating & comments', labelFa: 'امتیاز و نظرات' },
    { key: 'lesson-view', labelEn: 'View count', labelFa: 'تعداد بازدید' },
    { key: 'lesson-favorite', labelEn: 'Favorite button', labelFa: 'دکمه علاقه‌مندی' },
    { key: 'lesson-english-locked', labelEn: 'English-only hint', labelFa: 'راهنمای محتوای انگلیسی' },
    { key: 'lesson-journey', labelEn: 'Lesson journey', labelFa: 'مسیر درس' },
    { key: 'lesson-content', labelEn: 'Lesson content blocks', labelFa: 'بلوک‌های محتوای درس' },
    { key: 'lesson-tip', labelEn: 'Content tip', labelFa: 'نکته محتوا' },
    { key: 'lesson-sticky-audio', labelEn: 'Sticky audio row', labelFa: 'ردیف صوت چسبان' },
    { key: 'lesson-word-help', labelEn: 'Word help', labelFa: 'راهنمای واژه' },
    { key: 'lesson-word-help-scope', labelEn: 'Word help scope', labelFa: 'محدوده راهنمای واژه' },
    { key: 'lesson-practice', labelEn: 'Practice quizzes & exercises', labelFa: 'آزمون‌ها و تمرین‌های تمرینی' },
    { key: 'lesson-map-toggle', labelEn: 'Lesson map button', labelFa: 'دکمه نقشه درس' },
    { key: 'lesson-map-panel', labelEn: 'Lesson map panel', labelFa: 'پنل نقشه درس' },
  ],
  profile: [
    { key: 'profile-header', labelEn: 'Profile header', labelFa: 'سرصفحه پروفایل' },
    { key: 'profile-knowledge-placement', labelEn: 'Knowledge placement', labelFa: 'تعیین سطح دانش' },
    { key: 'profile-tab-profile', labelEn: 'Profile tab', labelFa: 'زبانه پروفایل' },
    { key: 'profile-tab-subscription', labelEn: 'Subscription tab', labelFa: 'زبانه اشتراک' },
    { key: 'profile-tab-messages', labelEn: 'Messages tab', labelFa: 'زبانه پیام‌ها' },
    { key: 'profile-tab-favorites', labelEn: 'Favorites tab', labelFa: 'زبانه علاقه‌مندی‌ها' },
    { key: 'profile-tab-comments', labelEn: 'Comments tab', labelFa: 'زبانه نظرات' },
    { key: 'profile-tab-goals', labelEn: 'Goals tab', labelFa: 'زبانه اهداف' },
    { key: 'profile-tab-progress', labelEn: 'Progress tab', labelFa: 'زبانه پیشرفت' },
  ],
  'tag-content': [
    { key: 'tag-content-header', labelEn: 'Tag header', labelFa: 'سرصفحه برچسب' },
    { key: 'tag-content-filters', labelEn: 'Tag navigation & filters', labelFa: 'ناوبری و فیلترهای برچسب' },
    { key: 'tag-content-viewer', labelEn: 'Tag lesson content', labelFa: 'محتوای درس برچسب' },
    { key: 'tag-content-tip', labelEn: 'Content tip', labelFa: 'نکته محتوا' },
    { key: 'tag-content-word-help', labelEn: 'Word help', labelFa: 'راهنمای واژه' },
    { key: 'tag-content-word-help-scope', labelEn: 'Word help scope', labelFa: 'محدوده راهنمای واژه' },
  ],
  'knowledge-placement': [
    { key: 'knowledge-placement-header', labelEn: 'Placement header', labelFa: 'سرصفحه تعیین سطح' },
    { key: 'knowledge-placement-intro', labelEn: 'How placement works', labelFa: 'روش تعیین سطح' },
    { key: 'knowledge-placement-choose-course', labelEn: 'Choose a course', labelFa: 'انتخاب دوره' },
    { key: 'knowledge-placement-courses', labelEn: 'Course list', labelFa: 'فهرست دوره‌ها' },
    { key: 'knowledge-placement-quiz', labelEn: 'Current lesson quiz', labelFa: 'آزمون درس جاری' },
    { key: 'knowledge-placement-take-quiz', labelEn: 'Take this quiz', labelFa: 'شرکت در این آزمون' },
    { key: 'knowledge-placement-skip', labelEn: 'Too easy — skip', labelFa: 'خیلی آسان — رد شدن' },
    { key: 'knowledge-placement-change-course', labelEn: 'Change course', labelFa: 'تغییر دوره' },
  ],
  quiz: [
    { key: 'quiz-flow-header', labelEn: 'Take quiz header', labelFa: 'سرصفحه آزمون' },
    { key: 'quiz-flow-filters', labelEn: 'Quiz filters', labelFa: 'فیلترهای آزمون' },
    { key: 'quiz-flow-take-quiz', labelEn: 'Take / retake quiz button', labelFa: 'دکمه شرکت / تکرار آزمون' },
    { key: 'quiz-flow-view-lesson', labelEn: 'View full lesson button', labelFa: 'دکمه مشاهده درس کامل' },
  ],
  'exercise-flow': [
    { key: 'exercise-flow-header', labelEn: 'Take exercise header', labelFa: 'سرصفحه تمرین' },
    { key: 'exercise-flow-filters', labelEn: 'Exercise filters', labelFa: 'فیلترهای تمرین' },
    { key: 'exercise-flow-view-lesson', labelEn: 'View full lesson button', labelFa: 'دکمه مشاهده درس کامل' },
    { key: 'exercise-flow-mark', labelEn: 'Mark as learned button', labelFa: 'دکمه علامت‌گذاری یادگرفته' },
    { key: 'exercise-flow-unmark', labelEn: 'Unmark button', labelFa: 'دکمه برداشتن علامت' },
    { key: 'exercise-flow-practice', labelEn: 'Practice button', labelFa: 'دکمه تمرین' },
  ],
  'quick-settings': QUICK_SETTINGS_WIRED_TARGETS,
  'quick-settings-security': QUICK_SETTINGS_WIRED_TARGETS.filter((t) => t.key.startsWith('quick-settings-security')),
  'quick-settings-notifications': QUICK_SETTINGS_WIRED_TARGETS.filter((t) => t.key.startsWith('quick-settings-notifications')),
  'quick-settings-lesson-text': QUICK_SETTINGS_WIRED_TARGETS.filter((t) => t.key.startsWith('quick-settings-lesson-text')),
  'quick-settings-region': QUICK_SETTINGS_WIRED_TARGETS.filter((t) => t.key.startsWith('quick-settings-region')),
  'quick-settings-template': QUICK_SETTINGS_WIRED_TARGETS.filter((t) => t.key.startsWith('quick-settings-template')),
  'quick-settings-simple': QUICK_SETTINGS_WIRED_TARGETS,
  'profile-profile': [
    { key: 'profile-header', labelEn: 'Profile header', labelFa: 'سرصفحه پروفایل' },
    { key: 'profile-knowledge-placement', labelEn: 'Knowledge placement', labelFa: 'تعیین سطح دانش' },
    { key: 'profile-tab-profile', labelEn: 'Profile tab', labelFa: 'زبانه پروفایل' },
  ],
  'profile-subscription': [
    { key: 'profile-tab-subscription', labelEn: 'Subscription tab', labelFa: 'زبانه اشتراک' },
  ],
  'profile-messages': [
    { key: 'profile-tab-messages', labelEn: 'Messages tab', labelFa: 'زبانه پیام‌ها' },
  ],
  'profile-favorites': [
    { key: 'profile-tab-favorites', labelEn: 'Favorites tab', labelFa: 'زبانه علاقه‌مندی‌ها' },
  ],
  'profile-comments': [
    { key: 'profile-tab-comments', labelEn: 'Comments tab', labelFa: 'زبانه نظرات' },
  ],
  'profile-goals': [
    { key: 'profile-tab-goals', labelEn: 'Goals tab', labelFa: 'زبانه اهداف' },
  ],
  'profile-progress': [
    { key: 'profile-tab-progress', labelEn: 'Progress tab', labelFa: 'زبانه پیشرفت' },
  ],
  leitner: [
    { key: 'leitner-panel', labelEn: 'Leitner box', labelFa: 'جعبه لایتنر' },
    { key: 'leitner-header', labelEn: 'Leitner header (drag to move)', labelFa: 'سرصفحه لایتنر (برای جابه‌جایی بکشید)' },
    { key: 'leitner-tab-due', labelEn: 'Due tab', labelFa: 'زبانه مرور' },
    { key: 'leitner-due-card', labelEn: 'Review card', labelFa: 'کارت مرور' },
    { key: 'leitner-tab-words', labelEn: 'My words tab', labelFa: 'زبانه واژه‌های من' },
    { key: 'leitner-word-list', labelEn: 'Word list', labelFa: 'فهرست واژه‌ها' },
    { key: 'leitner-tab-add', labelEn: 'Add tab', labelFa: 'زبانه افزودن' },
    { key: 'leitner-add-form', labelEn: 'Add word form', labelFa: 'فرم افزودن واژه' },
    { key: 'leitner-boxes', labelEn: 'Box counts', labelFa: 'شمارش خانه‌ها' },
  ],
  search: [
    { key: 'search-query', labelEn: 'Search query field', labelFa: 'فیلد عبارت جستجو' },
    { key: 'search-advanced-toggle', labelEn: 'Advanced filters button', labelFa: 'دکمه فیلترهای پیشرفته' },
    { key: 'search-filters', labelEn: 'Match filters row', labelFa: 'ردیف فیلترهای تطبیق' },
    { key: 'search-scope', labelEn: 'Search scope row', labelFa: 'ردیف محدوده جستجو' },
    { key: 'search-results', labelEn: 'Search results list', labelFa: 'فهرست نتایج جستجو' },
  ],
};

export function getSuggestedTargetsForPage(pageKey: string): WiredTourTarget[] {
  const pageTargets = WIRED_TOUR_TARGETS_BY_PAGE[pageKey] ?? [];
  const seen = new Set<string>();
  const merged: WiredTourTarget[] = [];
  for (const t of [...SHARED_WIRED_TOUR_TARGETS, ...pageTargets]) {
    if (seen.has(t.key)) continue;
    seen.add(t.key);
    merged.push(t);
  }
  return merged;
}

export function getWiredTourTarget(pageKey: string, targetKey: string): WiredTourTarget | undefined {
  return getSuggestedTargetsForPage(pageKey).find((t) => t.key === targetKey);
}

export function getTargetLabel(targetKey: string, pageKey: string, locale: 'en' | 'fa' = 'en'): string {
  const wired = getWiredTourTarget(pageKey, targetKey);
  if (!wired) return targetKey;
  return locale === 'fa' ? wired.labelFa ?? wired.labelEn ?? targetKey : wired.labelEn ?? targetKey;
}
